import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import SEO from '../../components/SEO'
import Breadcrumbs from '../../components/Breadcrumbs'
import { generateArticleSchema } from '../../lib/structured-data-utils'
import CtaSection from '../../components/CtaSection'
import { ICONS } from '../../components/icons/registry'

const WebDevelopmentAgencyDubai = () => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : 'https://demirix.com/blog/web-development-agency-dubai'
  const publishedDate = '2026-01-23'
  const modifiedDate = '2026-01-23'

  const articleSchema = generateArticleSchema({
    headline: 'How to Choose the Right Web Development Agency in Dubai',
    description: 'A comprehensive guide to selecting the best web development company in Dubai. Learn what factors to consider, questions to ask, and red flags to avoid when choosing your development partner.',
    datePublished: publishedDate,
    dateModified: modifiedDate,
    articleSection: 'Technology',
    keywords: [
      'web development company in dubai',
      'web development dubai',
      'web design dubai',
      'website development dubai',
      'dubai web agency',
      'web development uae',
      'best web developers dubai'
    ],
    wordCount: 3200,
    url: currentUrl
  })

  const breadcrumbs = [
    { name: 'Blog', path: '/blog' },
    { name: 'Web Development Agency Dubai', path: '/blog/web-development-agency-dubai' }
  ]

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  }

  const keyFactors = [
    {
      title: 'Portfolio and Case Studies',
      icon: ICONS.portfolio,
      description: 'A reputable web development company in Dubai should have a diverse portfolio demonstrating their capabilities across different industries and project types. Look for case studies that show measurable results, not just pretty designs.',
      keyPoints: [
        'Review projects similar to your industry or business model',
        'Check if they have experience with your target market (B2B, B2C, e-commerce)',
        'Look for evidence of measurable business outcomes, not just aesthetics',
        'Verify the portfolio is current and reflects modern design standards'
      ]
    },
    {
      title: 'Technology Stack and Expertise',
      icon: ICONS.techStack,
      description: 'The right web development agency should be proficient in modern technologies and frameworks. In Dubai\'s competitive market, you need a partner who can build scalable, secure, and performant web applications using cloud infrastructure and DevOps best practices.',
      keyPoints: [
        'Modern frameworks: React, Vue.js, Next.js, or Angular for frontend',
        'Robust backend solutions: Node.js, Python, PHP, or .NET',
        'Mobile responsiveness and Progressive Web App (PWA) capabilities',
        'Cloud infrastructure experience (AWS, Azure, Google Cloud)'
      ]
    },
    {
      title: 'Local Market Understanding',
      icon: ICONS.location,
      description: 'Dubai has unique market characteristics that international agencies often miss. A local software development company understands the cultural nuances, bilingual requirements (Arabic/English), and local business regulations.',
      keyPoints: [
        'RTL (Right-to-Left) design expertise for Arabic content',
        'Understanding of UAE e-commerce regulations and payment gateways',
        'Knowledge of local hosting requirements and data residency laws',
        'Experience with Dubai-specific industries: real estate, tourism, finance'
      ]
    },
    {
      title: 'Communication and Project Management',
      icon: ICONS.communication,
      description: 'Clear communication is essential for project success. The best web development companies in Dubai offer transparent processes, regular updates, and dedicated points of contact throughout the project lifecycle.',
      keyPoints: [
        'Dedicated project manager or account manager',
        'Regular status updates and milestone reviews',
        'Clear documentation and requirements gathering process',
        'Accessible during Dubai business hours (Sunday-Thursday)'
      ]
    },
    {
      title: 'Pricing Models and Transparency',
      icon: ICONS.costOptimisation,
      description: 'Web development costs in Dubai vary significantly. Understanding different pricing models helps you choose the approach that best fits your budget and project requirements.',
      keyPoints: [
        'Fixed price: Best for well-defined projects with clear scope',
        'Time and materials: Flexible for evolving requirements',
        'Retainer agreements: Ideal for ongoing development needs',
        'Transparent breakdown of costs with no hidden fees'
      ]
    },
    {
      title: 'Post-Launch Support and Maintenance',
      icon: ICONS.managedService,
      description: 'Your website launch is just the beginning. The right agency provides ongoing support, security updates, and continuous improvement to keep your digital presence competitive.',
      keyPoints: [
        'Service Level Agreements (SLAs) for response times',
        'Regular security patches and updates',
        'Performance monitoring and optimization',
        'Scalability planning as your business grows'
      ]
    }
  ]

  const questionsToAsk = [
    {
      question: 'Can you share case studies from clients in my industry?',
      reason: 'Industry-specific experience means they understand your unique challenges and can deliver solutions that address real business needs, not generic templates.'
    },
    {
      question: 'Who will be working on my project, and can I meet the team?',
      reason: 'Knowing your development team builds trust and ensures you have direct access to the people responsible for your project\'s success.'
    },
    {
      question: 'What is your development process from discovery to launch?',
      reason: 'A structured methodology indicates professionalism and reduces the risk of scope creep, delays, and budget overruns.'
    },
    {
      question: 'How do you handle changes or new requirements mid-project?',
      reason: 'Requirements often evolve. Understanding their change management process helps you anticipate costs and timeline impacts.'
    },
    {
      question: 'What security measures do you implement?',
      reason: 'With cyber threats increasing, your website must be secure from day one. HTTPS, data encryption, and secure coding practices are non-negotiable.'
    },
    {
      question: 'Do you provide training and documentation?',
      reason: 'You should be able to manage routine content updates independently. Proper training and documentation empower your team and reduce ongoing costs.'
    },
    {
      question: 'What are your payment terms and milestone structure?',
      reason: 'Milestone-based payments protect both parties and ensure you only pay for completed, approved work.'
    },
    {
      question: 'Can you provide references from recent Dubai clients?',
      reason: 'Speaking directly with past clients gives you honest insights into the agency\'s reliability, quality, and professionalism.'
    }
  ]

  const redFlags = [
    {
      title: 'Unrealistically Low Prices',
      description: 'If a quote seems too good to be true, it probably is. Extremely low prices often mean inexperienced developers, offshore outsourcing without disclosure, or cutting corners on quality and security.',
      warning: 'Quality web development in Dubai typically starts from AED 15,000 for basic websites to AED 100,000+ for complex web applications.'
    },
    {
      title: 'No Portfolio or Vague References',
      description: 'Any legitimate web development company should proudly showcase their work. If they cannot provide examples or references, question their experience and track record.',
      warning: 'Always verify portfolio claims by visiting the actual websites and contacting references directly.'
    },
    {
      title: 'Guaranteed First-Page Google Rankings',
      description: 'No one can guarantee specific search engine rankings. While good development practices support SEO, rankings depend on many factors beyond any agency\'s control.',
      warning: 'Beware of agencies promising instant SEO results. Sustainable rankings take months of consistent effort.'
    },
    {
      title: 'Poor Communication During Sales',
      description: 'If an agency is slow to respond or unclear during the sales process, expect worse during the project. Communication quality during initial discussions reflects their overall professionalism.',
      warning: 'Response times exceeding 48 hours and vague answers to direct questions are warning signs.'
    },
    {
      title: 'No Written Contracts or SOWs',
      description: 'Professional agencies always provide detailed Statements of Work (SOW) and contracts. Without written agreements, you have no recourse if things go wrong.',
      warning: 'Never begin a project without a signed contract specifying deliverables, timelines, costs, and ownership rights.'
    },
    {
      title: 'Ownership and IP Concerns',
      description: 'Ensure you will own the final code and all assets. Some agencies retain ownership or use proprietary systems that lock you in.',
      warning: 'Verify in writing that you own all code, designs, and content upon final payment.'
    }
  ]

  const localVsOffshore = [
    {
      aspect: 'Communication',
      local: 'Same timezone, face-to-face meetings possible, cultural understanding',
      offshore: 'Time zone challenges, video calls only, potential cultural misalignment'
    },
    {
      aspect: 'Market Knowledge',
      local: 'Deep understanding of UAE market, regulations, and consumer behavior',
      offshore: 'Limited knowledge of local market nuances and requirements'
    },
    {
      aspect: 'Language Support',
      local: 'Native Arabic/English bilingual capabilities, RTL expertise',
      offshore: 'May require additional investment for Arabic localization'
    },
    {
      aspect: 'Legal Protection',
      local: 'UAE contract law applies, easier legal recourse if needed',
      offshore: 'Complex international legal jurisdiction issues'
    },
    {
      aspect: 'Ongoing Support',
      local: 'Quick response during Dubai business hours, on-site visits possible',
      offshore: 'Support may be available at inconvenient hours'
    },
    {
      aspect: 'Cost',
      local: 'Higher upfront investment, often better long-term value',
      offshore: 'Lower initial cost, but potential hidden costs in revisions and communication'
    }
  ]

  return (
    <div className="pt-20 bg-white dark:bg-gray-900">
      <SEO
        title="How to Choose the Right Web Development Agency in Dubai | Guide 2026"
        description="Complete guide to selecting the best web development company in Dubai. Learn key factors to consider, essential questions to ask, red flags to avoid, and benefits of working with a local Dubai agency."
        keywords={[
          'web development company in dubai',
          'web development dubai',
          'web design dubai',
          'website development dubai',
          'dubai web agency',
          'web development uae',
          'best web developers dubai',
          'web design company dubai',
          'website design dubai',
          'digital agency dubai',
          'web development services dubai',
          'ecommerce development dubai'
        ]}
        url={currentUrl}
        type="article"
        author="Demirix"
        publishedTime={publishedDate}
        structuredData={articleSchema}
      />
      <Breadcrumbs customCrumbs={breadcrumbs} />

      {/* Hero Section */}
      <article>
        <header className="bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 py-16 md:py-24">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <span className="inline-block px-4 py-2 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium mb-6">
                Web Development Guide
              </span>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
                How to Choose the Right Web Development Agency in Dubai
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 mb-8">
                A comprehensive guide to finding and evaluating the best web development company for your business in Dubai's competitive digital market
              </p>
              <div className="flex items-center justify-center gap-6 text-sm text-gray-500 dark:text-gray-400">
                <time dateTime={publishedDate}>Published: January 23, 2026</time>
                <span className="hidden sm:inline">|</span>
                <span className="hidden sm:inline">18 min read</span>
              </div>
            </motion.div>
          </div>
        </header>

        {/* Introduction */}
        <section className="section-padding">
          <div className="container-custom">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={containerVariants}
              className="max-w-4xl mx-auto"
            >
              <motion.div variants={itemVariants} className="prose prose-lg dark:prose-invert max-w-none">
                <p className="text-xl text-gray-700 dark:text-gray-200 leading-relaxed mb-6">
                  Dubai's digital landscape is booming. With over 98% internet penetration and a government pushing for digital transformation
                  through initiatives like Smart Dubai, having a strong online presence is no longer optional - it's essential for business survival.
                  But with hundreds of agencies claiming to be the best <strong>web development company in Dubai</strong>, how do you separate
                  the experts from the amateurs?
                </p>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                  Whether you're a startup launching your first website, an established business looking for a redesign, or an enterprise
                  requiring a complex web application, choosing the wrong development partner can cost you months of delays, thousands of
                  dirhams in wasted budget, and potentially damage your brand reputation. Working with experienced <Link to="/it-consulting-companies-dubai" className="text-primary-600 dark:text-primary-400 hover:underline">IT consulting companies in Dubai</Link> can help mitigate these risks.
                </p>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  This guide will walk you through everything you need to know about selecting a <strong>web development agency in Dubai</strong>.
                  From evaluating portfolios and understanding pricing models to spotting red flags and maximizing the benefits of working with
                  a local agency, we'll help you make an informed decision that sets your project up for success. For insights into the broader tech landscape, check out our guide on <Link to="/blog/dubai-software-trends-2026" className="text-primary-600 dark:text-primary-400 hover:underline">software development trends in Dubai</Link>.
                </p>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Key Factors Section */}
        <section className="section-padding bg-gray-50 dark:bg-gray-800">
          <div className="container-custom">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={containerVariants}
            >
              <motion.div variants={itemVariants} className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                  Key Factors to Consider When Choosing a Web Development Company
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                  These critical factors will help you evaluate and compare <strong>web development companies in Dubai</strong>
                </p>
              </motion.div>

              <div className="space-y-12">
                {keyFactors.map((factor, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="bg-white dark:bg-gray-700 rounded-2xl p-8 shadow-lg border border-gray-100 dark:border-gray-600"
                  >
                    <div className="flex flex-col md:flex-row md:items-start gap-6">
                      <div className="w-14 h-14 flex-shrink-0" aria-hidden="true">
                        <factor.icon className="w-full h-full" darkTreatment="none" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                          {factor.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                          {factor.description}
                        </p>
                        <ul className="space-y-3">
                          {factor.keyPoints.map((point, pointIndex) => (
                            <li key={pointIndex} className="flex items-start">
                              <svg className="w-5 h-5 text-primary-600 dark:text-primary-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span className="text-gray-700 dark:text-gray-200">{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Questions to Ask Section */}
        <section className="section-padding">
          <div className="container-custom">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={containerVariants}
            >
              <motion.div variants={itemVariants} className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                  Essential Questions to Ask Potential Agencies
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                  Use these questions during your evaluation to uncover important information about each <strong>web design Dubai</strong> agency
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
                {questionsToAsk.map((item, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-700 rounded-2xl p-6 border border-gray-200 dark:border-gray-600"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-primary-600 dark:bg-primary-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                          {index + 1}
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                          "{item.question}"
                        </h3>
                        <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                          <span className="font-medium text-primary-600 dark:text-primary-400">Why it matters: </span>
                          {item.reason}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Red Flags Section */}
        <section className="section-padding bg-gray-50 dark:bg-gray-800">
          <div className="container-custom">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={containerVariants}
            >
              <motion.div variants={itemVariants} className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                  Red Flags to Watch Out For
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                  Warning signs that indicate you should look elsewhere for your <strong>web development Dubai</strong> partner
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {redFlags.map((flag, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="bg-white dark:bg-gray-700 rounded-2xl p-6 shadow-lg border-l-4 border-red-500"
                  >
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                      {flag.title}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-4">
                      {flag.description}
                    </p>
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 dark:border-red-800">
                      <p className="text-red-700 dark:text-red-300 text-xs font-medium">
                        <span className="font-bold">Warning: </span>{flag.warning}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Local vs Offshore Section */}
        <section className="section-padding">
          <div className="container-custom">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={containerVariants}
            >
              <motion.div variants={itemVariants} className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                  Dubai-Based Agency vs. Offshore: A Comparison
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                  Understanding the trade-offs between working with a local <strong>web development company in Dubai</strong> versus offshore alternatives
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="max-w-5xl mx-auto">
                <div className="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-600">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-700">
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white">
                          Aspect
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-primary-600 dark:text-primary-400">
                          Dubai-Based Agency
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">
                          Offshore Agency
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                      {localVsOffshore.map((row, index) => (
                        <tr key={index} className="bg-white dark:bg-gray-800">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                            {row.aspect}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                            <span className="flex items-start">
                              <svg className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              {row.local}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                            {row.offshore}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-8 bg-primary-50 dark:bg-primary-900/20 rounded-2xl p-8 border border-primary-100 dark:border-primary-800">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                    The Bottom Line on Local vs. Offshore
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    While offshore agencies may offer lower initial quotes, the total cost of a project often ends up similar or higher
                    when you factor in communication overhead, revision cycles, and potential quality issues. For businesses operating in
                    Dubai, working with a local <strong>web development company</strong> typically delivers better value through faster
                    turnaround, easier collaboration, and solutions tailored specifically to the UAE market.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Conclusion Section */}
        <section className="section-padding bg-gray-50 dark:bg-gray-800">
          <div className="container-custom">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={containerVariants}
              className="max-w-4xl mx-auto"
            >
              <motion.div variants={itemVariants}>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-8 text-center">
                  Making Your Final Decision
                </h2>
                <div className="prose prose-lg dark:prose-invert max-w-none">
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                    Choosing the right <strong>web development agency in Dubai</strong> is one of the most important business decisions
                    you'll make for your digital presence. The ideal partner should feel like an extension of your team - understanding
                    your business goals, communicating transparently, and delivering solutions that drive real results.
                  </p>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                    Take your time during the selection process. Meet with multiple agencies, ask tough questions, and verify their
                    claims through references and portfolio review. Remember that the cheapest option rarely delivers the best value,
                    and the most expensive doesn't guarantee quality either. Focus on finding a partner whose expertise, communication
                    style, and values align with your business.
                  </p>
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                    Dubai's digital economy continues to grow, and your website is often the first impression potential customers have
                    of your business. Invest in a partnership that will create a digital asset you're proud of - one that represents your
                    brand professionally and helps you achieve your business objectives in one of the world's most dynamic markets. If you need
                    ongoing support, consider <Link to="/managed-it-services-dubai" className="text-primary-600 dark:text-primary-400 hover:underline">managed IT services in Dubai</Link> to keep your digital presence running smoothly.
                  </p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Related Services Section */}
        <section className="section-padding bg-gray-50 dark:bg-gray-800">
          <div className="container-custom">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={containerVariants}
              className="max-w-5xl mx-auto"
            >
              <motion.div variants={itemVariants} className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Related Services
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                  Our web development and technology services for Dubai businesses
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Link
                  to="/software-development-dubai"
                  className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600 group"
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    Software Development in Dubai
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    Custom web applications and software solutions for Dubai businesses.
                  </p>
                </Link>

                <Link
                  to="/ecommerce-development-dubai"
                  className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600 group"
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    E-commerce Development in Dubai
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    Online stores and e-commerce platforms tailored for the UAE market.
                  </p>
                </Link>

                <Link
                  to="/managed-it-services-dubai"
                  className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600 group"
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    Managed IT Services in Dubai
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    Ongoing support, maintenance, and IT management services.
                  </p>
                </Link>

                <Link
                  to="/startup-tech-consulting-dubai"
                  className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600 group"
                >
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    Startup Tech Consulting
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    Technical guidance and MVP development for Dubai startups.
                  </p>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Related Articles Section */}
        <section className="section-padding">
          <div className="container-custom">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={containerVariants}
            >
              <motion.div variants={itemVariants} className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Related Articles
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                  Continue exploring insights about technology and business in Dubai
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
                <Link
                  to="/blog/dubai-software-trends-2026"
                  className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600 group"
                >
                  <span className="inline-block px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-xs font-medium mb-3">
                    Technology Insights
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    Top Software Development Trends in Dubai 2026
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    Discover the technologies shaping Dubai's software landscape.
                  </p>
                </Link>

                <Link
                  to="/blog/mobile-app-development-cost-dubai"
                  className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600 group"
                >
                  <span className="inline-block px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-xs font-medium mb-3">
                    Mobile Apps
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    Mobile App Development Cost in Dubai 2026
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    Complete pricing guide for app development in the UAE market.
                  </p>
                </Link>

                <Link
                  to="/blog/digital-transformation-uae"
                  className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600 group"
                >
                  <span className="inline-block px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-xs font-medium mb-3">
                    Digital Transformation
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    Digital Transformation in UAE: Business Guide
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    How UAE businesses are leveraging digital technologies.
                  </p>
                </Link>

                <Link
                  to="/blog/enterprise-software-dubai"
                  className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600 group"
                >
                  <span className="inline-block px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-xs font-medium mb-3">
                    Enterprise Solutions
                  </span>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    Enterprise Software Solutions for Dubai
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    ERP, CRM, and business software for UAE enterprises.
                  </p>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <CtaSection
          heading="Looking for a Trusted Web Development Partner in Dubai?"
          description="Let's discuss your project requirements. We offer free consultations to understand your needs and provide honest recommendations - even if we're not the right fit for your project."
          primary={{ kind: 'calendly', label: 'Schedule a Free Consultation' }}
          secondary={{ kind: 'link', label: 'View Our Services', href: '/services' }}
        />

        {/* Related Content */}
        <section className="section-padding">
          <div className="container-custom">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={containerVariants}
            >
              <motion.div variants={itemVariants} className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Continue Learning
                </h2>
              </motion.div>

              <motion.div variants={itemVariants} className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                <Link
                  to="/blog/dubai-software-trends-2026"
                  className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600 group"
                >
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    Software Trends 2026
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    Discover the top software development trends shaping Dubai's tech landscape this year.
                  </p>
                </Link>

                <Link
                  to="/dubai-services"
                  className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600 group"
                >
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    Dubai Services
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    Explore our specialized technology solutions for businesses in Dubai and the UAE.
                  </p>
                </Link>

                <Link
                  to="/contact"
                  className="bg-white dark:bg-gray-700 rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600 group"
                >
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                    Contact Us
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    Get in touch to discuss your web development project requirements.
                  </p>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </article>
    </div>
  )
}

export default WebDevelopmentAgencyDubai
